import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Mail, Loader2, AlertTriangle, Power, Plus, Trash2, Save, FileSpreadsheet, ExternalLink,
} from 'lucide-react';
import CpscBrokerScope from './CpscBrokerScope'; // CHA-271 — the production-scope manager (lives with the broker-report config)

/**
 * CpscEmailRecipients — CHA-233. Recipients + master kill switch for the daily broker-report cron
 * (sendDailyBrokerReport, 10:30 AM ET). Two role tabs:
 *   - Broker   → receives the COMPLETE rows (certified/exempt) email
 *   - Operator → receives the INCOMPLETE (needs-cert) rows email
 * Each role is a table of { label, email, active }. The master ON/OFF toggle gates the WHOLE daily send and
 * defaults OFF — nothing sends until an admin turns it on. Reads/writes go through the admin-gated, tenant-
 * scoped callables listCpscEmailRecipients / manageCpscEmailRecipients (server enforces the admin gate; this
 * component also hides the surface from non-admins). A role's email only sends when it has ≥1 ACTIVE recipient.
 */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
// CHA-253 — client-side mirror of the server's sheetUrl validation (canonical URL or bare id; server re-validates).
const SHEET_URL_RE = /\/spreadsheets\/d\/[A-Za-z0-9_-]+/;
const BARE_SHEET_ID_RE = /^[A-Za-z0-9_-]{20,}$/;
const ROLES = [
  { id: 'broker', label: 'Broker', blurb: 'Receives the COMPLETE rows (certified or exempt) email.' },
  { id: 'operator', label: 'Operator', blurb: 'Receives the INCOMPLETE rows (needs cert) email.' },
];
const s = (v) => String(v ?? '').trim();

export default function CpscEmailRecipients({ userProfile } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';
  const arg = useCallback((extra = {}) => (isPlatformRole ? { tenantId, ...extra } : { ...extra }), [isPlatformRole, tenantId]);

  const [enabled, setEnabled] = useState(false);
  const [lists, setLists] = useState({ broker: [], operator: [] });
  const [activeTab, setActiveTab] = useState('broker');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'enabled' | 'broker' | 'operator' | 'sheet'
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [sheetUrl, setSheetUrl] = useState('');      // CHA-253 — the input value
  const [savedSheetUrl, setSavedSheetUrl] = useState(''); // last saved value (drives the "open sheet" link)
  const [podDenylist, setPodDenylist] = useState('');      // CHA-259 — comma-separated input value
  const [savedPodDenylist, setSavedPodDenylist] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await httpsCallable(getFunctions(), 'listCpscEmailRecipients')(arg());
      const d = res?.data || {};
      setEnabled(d.enabled === true);
      setLists({
        broker: Array.isArray(d.broker) ? d.broker.map((r) => ({ label: s(r.label), email: s(r.email), active: r.active === true })) : [],
        operator: Array.isArray(d.operator) ? d.operator.map((r) => ({ label: s(r.label), email: s(r.email), active: r.active === true })) : [],
      });
      setSheetUrl(s(d.sheetUrl));
      setSavedSheetUrl(s(d.sheetUrl));
      const denyStr = Array.isArray(d.podDenylist) ? d.podDenylist.join(', ') : '';
      setPodDenylist(denyStr);
      setSavedPodDenylist(denyStr);
    } catch (e) { setError(e?.message || 'Could not load recipients.'); } finally { setLoading(false); }
  }, [arg]);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  const toggleEnabled = useCallback(async () => {
    const next = !enabled;
    setBusy('enabled'); setError(null); setNotice(null);
    try {
      await httpsCallable(getFunctions(), 'manageCpscEmailRecipients')(arg({ enabled: next }));
      setEnabled(next);
      setNotice(`Daily broker report turned ${next ? 'ON' : 'OFF'}.`);
    } catch (e) { setError(e?.message || 'Could not change the kill switch.'); } finally { setBusy(null); }
  }, [arg, enabled]);

  const saveRole = useCallback(async (roleId) => {
    const recipients = lists[roleId];
    // Client-side validation mirrors the server: every row needs a valid email (server re-validates).
    for (const r of recipients) {
      if (!EMAIL_RE.test(s(r.email))) { setError(`${roleId}: "${r.email || '(blank)'}" is not a valid email.`); return; }
    }
    setBusy(roleId); setError(null); setNotice(null);
    try {
      await httpsCallable(getFunctions(), 'manageCpscEmailRecipients')(arg({ role: roleId, recipients }));
      setNotice(`${roleId.charAt(0).toUpperCase() + roleId.slice(1)} recipients saved.`);
      await load();
    } catch (e) { setError(e?.message || 'Save failed.'); } finally { setBusy(null); }
  }, [arg, lists, load]);

  // CHA-253 — reset the per-tab safe-fail counters (sheetLastGood). The one legitimate use: the Certified
  // registry shrank past the monotonicity floor for a REAL reason (e.g. operator bulk-deleted bad drafts)
  // and the sentinel now skips it as suspicious — reset lets the next run write fresh. Counters themselves
  // are cron-owned; the server only accepts the literal null (reset), never client-set values.
  const resetSheetCounters = useCallback(async () => {
    setBusy('sheetReset'); setError(null); setNotice(null);
    try {
      await httpsCallable(getFunctions(), 'manageCpscEmailRecipients')(arg({ sheetLastGood: null }));
      setNotice('Sheet safe-fail counters reset — the next daily run writes all tabs fresh.');
    } catch (e) { setError(e?.message || 'Could not reset the sheet counters.'); } finally { setBusy(null); }
  }, [arg]);

  // CHA-259 — save the confirmed-non-US POD denylist (compliance-scope control; server audits every edit).
  const savePodDenylist = useCallback(async () => {
    const codes = podDenylist.split(',').map((v) => s(v).toUpperCase()).filter(Boolean);
    if (codes.some((v) => !/^[A-Z]{2,5}$/.test(v))) {
      setError('POD denylist entries must be 2-5 letter port codes (comma-separated).');
      return;
    }
    setBusy('pods'); setError(null); setNotice(null);
    try {
      await httpsCallable(getFunctions(), 'manageCpscEmailRecipients')(arg({ podDenylist: codes }));
      setNotice('Non-US POD denylist saved (edit audited) — the next run excludes only these confirmed-foreign ports.');
      await load();
    } catch (e) { setError(e?.message || 'Could not save the POD denylist.'); } finally { setBusy(null); }
  }, [arg, podDenylist, load]);

  // CHA-253 — save the broker-status Google Sheet URL ('' clears the sheet sync). Server extracts + stores
  // the spreadsheet ID; the URL itself is a non-secret ref (Google sharing gates access, not the link).
  const saveSheetUrl = useCallback(async () => {
    const v = s(sheetUrl);
    if (v && !SHEET_URL_RE.test(v) && !BARE_SHEET_ID_RE.test(v)) {
      setError('Sheet URL must be a Google Sheets link (…/spreadsheets/d/<id>/…) or a bare spreadsheet id.');
      return;
    }
    setBusy('sheet'); setError(null); setNotice(null);
    try {
      await httpsCallable(getFunctions(), 'manageCpscEmailRecipients')(arg({ sheetUrl: v }));
      setNotice(v ? 'Broker-status sheet saved — the daily run will refresh it.' : 'Broker-status sheet cleared — the daily run will skip the sheet.');
      await load();
    } catch (e) { setError(e?.message || 'Could not save the sheet URL.'); } finally { setBusy(null); }
  }, [arg, sheetUrl, load]);

  const setRow = (roleId, idx, patch) => setLists((cur) => ({
    ...cur, [roleId]: cur[roleId].map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  }));
  const addRow = (roleId) => setLists((cur) => ({ ...cur, [roleId]: [...cur[roleId], { label: '', email: '', active: true }] }));
  const removeRow = (roleId, idx) => setLists((cur) => ({ ...cur, [roleId]: cur[roleId].filter((_, i) => i !== idx) }));

  const activeCount = useMemo(() => ({
    broker: lists.broker.filter((r) => r.active && EMAIL_RE.test(s(r.email))).length,
    operator: lists.operator.filter((r) => r.active && EMAIL_RE.test(s(r.email))).length,
  }), [lists]);

  if (!isAdmin) {
    return <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">Email Recipients is restricted to administrators.</div>;
  }

  const list = lists[activeTab];
  const roleMeta = ROLES.find((r) => r.id === activeTab);

  return (
    <div className="font-plex text-carbon-ink space-y-4 max-w-3xl">
      <div>
        <h3 className="text-base font-semibold text-carbon-ink flex items-center gap-1.5"><Mail size={16} /> Email Recipients</h3>
        <p className="text-sm text-carbon-gray-70">
          Who receives the automated daily broker report (10:30 AM ET). <strong>Broker</strong> recipients get the
          complete rows; <strong>Operator</strong> recipients get the incomplete (needs-cert) rows. A role's email
          only sends when it has at least one <strong>active</strong> recipient.
        </p>
      </div>

      {notice && <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-1.5">{notice}</div>}
      {error && <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1"><AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{error}</span></div>}

      {/* Master kill switch */}
      <div className={`bg-carbon-white border border-carbon-border border-l-[3px] p-4 flex items-center justify-between gap-3 ${enabled ? 'border-l-carbon-green-50' : 'border-l-carbon-orange-40'}`}>
        <div className="flex items-center gap-3">
          <Power size={18} className={enabled ? 'text-carbon-green-50' : 'text-carbon-orange-70'} />
          <div>
            <div className="text-sm font-semibold text-carbon-ink">
              Daily broker report:{' '}
              <span className={`font-plex-mono ${enabled ? 'text-carbon-green-50' : 'text-carbon-orange-70'}`}>{enabled ? 'ON' : 'OFF'}</span>
            </div>
            <div className="text-xs text-carbon-gray-70">
              {enabled ? 'The cron sends the broker + operator emails each morning.' : 'The cron sends nothing until this is turned on (safe default).'}
            </div>
          </div>
        </div>
        <button
          onClick={toggleEnabled} disabled={busy === 'enabled' || loading}
          aria-pressed={enabled}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${enabled ? 'bg-carbon-red-60 hover:opacity-90' : 'bg-carbon-green-50 hover:opacity-90'}`}
        >
          {busy === 'enabled' ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />} Turn {enabled ? 'OFF' : 'ON'}
        </button>
      </div>

      {/* CHA-253 — the broker-status Google Sheet (refreshed by the same daily run; rides the master switch) */}
      <div className="bg-carbon-white border border-carbon-border p-4 space-y-2">
        <div className="flex items-center gap-2">
          <FileSpreadsheet size={16} className="text-carbon-gray-70" />
          <span className="text-sm font-semibold text-carbon-ink">Broker status Google Sheet</span>
          {savedSheetUrl && (
            <a href={savedSheetUrl} target="_blank" rel="noopener noreferrer" className="ml-auto flex items-center gap-1 text-xs text-carbon-blue-60 hover:text-carbon-blue-70 hover:underline">
              Open sheet <ExternalLink size={12} />
            </a>
          )}
        </div>
        <p className="text-xs text-carbon-gray-70">
          The daily run also refreshes a status spreadsheet (Complete / Incomplete / Certified per company).
          Create the sheet, share it as <strong>Editor</strong> with the functions service account, then paste its
          URL here. Clearing the field turns the sheet refresh off; tabs are never blanked on a bad run (safe-fail).
        </p>
        <div className="flex items-center gap-2">
          <input
            value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/…"
            className="flex-1 border border-carbon-gray-30 bg-carbon-gray-10 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-carbon-blue-60" aria-label="sheet url"
          />
          <button onClick={saveSheetUrl} disabled={busy === 'sheet' || loading || s(sheetUrl) === savedSheetUrl}
            className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50">
            {busy === 'sheet' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
        </div>
        {savedSheetUrl && (
          <div className="flex items-center justify-between gap-2 pt-1 border-t border-carbon-gray-10">
            <span className="text-xs text-carbon-gray-50">
              If a tab is stuck showing old data after a legitimate cleanup (e.g. bulk-deleted certs), reset the
              safe-fail counters — the next daily run rewrites every tab fresh.
            </span>
            <button onClick={resetSheetCounters} disabled={busy === 'sheetReset' || loading}
              className="shrink-0 flex items-center gap-1 px-3 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-50">
              {busy === 'sheetReset' ? <Loader2 size={12} className="animate-spin" /> : null} Reset safe-fail counters
            </button>
          </div>
        )}
      </div>

      {/* CHA-259 — confirmed-non-US POD denylist (compliance-scope gate; fail-safe: unknown ports stay in scope) */}
      <div className="bg-carbon-white border border-carbon-border p-4 space-y-2">
        <span className="text-sm font-semibold text-carbon-ink">Non-US discharge ports (POD denylist)</span>
        <p className="text-xs text-carbon-gray-70">
          Invoices whose <strong>every</strong> known discharge port is on this list are excluded from the CPSC
          worklist (they never enter the US). Unknown ports and unmatched invoices always <strong>stay in scope</strong> —
          when in doubt, leave a port off. Every edit is audited; exclusions appear in each run&apos;s audit record.
        </p>
        <div className="flex items-center gap-2">
          <input
            value={podDenylist} onChange={(e) => setPodDenylist(e.target.value)}
            placeholder="e.g. VAN, HAM, SOU"
            className="flex-1 border border-carbon-gray-30 bg-carbon-gray-10 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-carbon-blue-60" aria-label="pod denylist"
          />
          <button onClick={savePodDenylist} disabled={busy === 'pods' || loading || s(podDenylist) === savedPodDenylist}
            className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50">
            {busy === 'pods' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
        </div>
      </div>

      {/* Role tabs */}
      <div className="flex items-center gap-1 border-b border-carbon-border">
        {ROLES.map((r) => (
          <button
            key={r.id} onClick={() => setActiveTab(r.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${activeTab === r.id ? 'border-carbon-blue-60 text-carbon-ink font-semibold' : 'border-transparent text-carbon-gray-70 font-medium hover:text-carbon-ink'}`}
          >
            {r.label} <span className="font-plex-mono text-[10.5px] text-carbon-gray-50">({activeCount[r.id]} active)</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-carbon-gray-70 px-1 py-6"><Loader2 size={15} className="animate-spin" /> Loading recipients…</div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-carbon-gray-70">{roleMeta?.blurb}</p>
          {activeCount[activeTab] === 0 && (
            <div className="text-xs text-carbon-orange-70 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-orange-40 px-3 py-2 flex items-start gap-1">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" /> No active recipient for this role — its email will be skipped until you add one.
            </div>
          )}

          <div className="bg-carbon-white border border-carbon-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-carbon-gray-10 font-plex-mono text-[10px] uppercase tracking-[0.12em] text-carbon-gray-50 text-left border-b border-carbon-border">
                <tr>
                  <th className="px-3 py-2 font-medium">Label</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium text-center">Active</th>
                  <th className="px-3 py-2 font-medium w-10" />
                </tr>
              </thead>
              <tbody>
                {list.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-carbon-gray-50">No {activeTab} recipients yet. Add one below.</td></tr>
                )}
                {list.map((r, idx) => (
                  <tr key={idx} className="border-t border-carbon-gray-10">
                    <td className="px-3 py-1.5">
                      <input value={r.label} onChange={(e) => setRow(activeTab, idx, { label: e.target.value })} placeholder="e.g. Primary Broker"
                        className="w-full border border-carbon-gray-30 bg-carbon-gray-10 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-carbon-blue-60" aria-label="recipient label" />
                    </td>
                    <td className="px-3 py-1.5">
                      <input value={r.email} onChange={(e) => setRow(activeTab, idx, { email: e.target.value })} placeholder="name@example.com"
                        className={`w-full border bg-carbon-gray-10 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-carbon-blue-60 ${r.email && !EMAIL_RE.test(s(r.email)) ? 'border-carbon-red-60' : 'border-carbon-gray-30'}`} aria-label="recipient email" />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <input type="checkbox" checked={r.active} onChange={(e) => setRow(activeTab, idx, { active: e.target.checked })} aria-label={`Active ${r.email}`} />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <button onClick={() => removeRow(activeTab, idx)} title="Remove" className="text-carbon-gray-40 hover:text-carbon-red-60"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => addRow(activeTab)} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10">
              <Plus size={14} /> Add recipient
            </button>
            <div className="flex-1" />
            <button onClick={() => saveRole(activeTab)} disabled={busy === activeTab}
              className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50">
              {busy === activeTab ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save {roleMeta?.label} recipients
            </button>
          </div>
        </div>
      )}
      {/* CHA-271 — the production-scope gate manager (config lives on this same recipients doc) */}
      <CpscBrokerScope arg={arg} />

    </div>
  );
}
